import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error from "../components/Error";
import SaveButton from "../components/SaveButton";
import ShareButton from "../components/ShareButton";
import { SkeletonEvent } from "../components/Skeleton";
import { formatCardDate, priceBadge } from "../components/Card";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import { fetchEvent } from "../api";
import { sanitizeHTML } from "../utils/sanitize";
import "./Event.css";
import pictoPmr from "../images/Deficients-moteur-RVB.jpg";
import pictoBlind from "../images/Deficients-visuels-RVB.jpg";
import pictoDeaf from "../images/Deficients-auditifs-RVB.jpg";

function sanitizeDateDescription(dateDescription) {
  return (dateDescription || "").replace(/de {2}à/g, "");
}

function priceLabel(item, hasPriceDetail) {
  if (item.price_type === "gratuit") return "Gratuit";
  if (item.price_type === "payant" && !hasPriceDetail) return "Payant";
  return null;
}

function Event() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [item, setItem] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const descriptionHTML = useMemo(
    () => sanitizeHTML(item.description),
    [item.description]
  );
  const dateDescriptionHTML = useMemo(
    () => sanitizeHTML(sanitizeDateDescription(item.date_description)),
    [item.date_description]
  );
  const priceDetailHTML = useMemo(
    () => sanitizeHTML(item.price_detail),
    [item.price_detail]
  );
  const transportHTML = useMemo(
    () => sanitizeHTML(item.transport),
    [item.transport]
  );

  function searchByTag(tag) {
    navigate(`/search?q=${encodeURIComponent(tag)}`);
  }

  function renderTags(tags) {
    return (
      <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0">
        {tags.map((tag) => (
          <li key={tag}>
            <button
              type="button"
              className="pa-chip pa-chip--tag"
              onClick={() => searchByTag(tag)}
            >
              {tag}
            </button>
          </li>
        ))}
      </ul>
    );
  }

  useEffect(() => {
    const ac = new AbortController();
    setIsLoaded(false);
    setError(null);

    fetchEvent(id, ac.signal)
      .then((result) => {
        setItem(result.record.fields);
        setIsLoaded(true);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setIsLoaded(true);
      });

    return () => ac.abort();
  }, [id, reloadKey]);

  useEffect(() => {
    if (item.title) document.title = `${item.title} — Paris Agenda`;
    return () => {
      document.title = "Paris Agenda";
    };
  }, [item.title]);

  const hasPriceDetail =
    typeof item.price_detail === "string" &&
    item.price_detail.replace(/<[^>]*>/g, "").trim().length > 0;

  const priceTopLine = priceLabel(item, hasPriceDetail);

  const accessibilityLabels = [
    item.pmr === 1 && {
      src: pictoPmr,
      alt: "Accessible aux personnes à mobilité réduite",
    },
    item.blind === 1 && {
      src: pictoBlind,
      alt: "Accessible aux personnes déficientes visuelles",
    },
    item.deaf === 1 && {
      src: pictoDeaf,
      alt: "Accessible aux personnes déficientes auditives",
    },
  ].filter(Boolean);

  if (error) {
    return (
      <div className="container">
        <Error err={error} onRetry={() => setReloadKey((k) => k + 1)} />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="container pa-event">
        <SkeletonEvent />
      </div>
    );
  }

  const headerDate = formatCardDate(item.date_start, item.date_end);
  const headerPrice = priceBadge(item.price_type);

  return (
    <article className="container pa-event">
      <header className="pa-event__head">
        <div className="pa-event__meta">
          {headerDate && <p className="pa-eyebrow">{headerDate}</p>}
          {headerPrice && (
            <span className={`pa-badge ${headerPrice.variant}`}>
              {headerPrice.label}
            </span>
          )}
        </div>
        <h1 className="pa-event__title">{item.title}</h1>
        {item.lead_text && (
          <p className="pa-lead prose pa-event__lead">{item.lead_text}</p>
        )}
        {item.tags && item.tags.length > 0 && (
          <div className="pa-event__tags">{renderTags(item.tags)}</div>
        )}
      </header>

      {/* Mobile-only quick summary so action info stays at the top. */}
      <div className="pa-panel pa-panel--soft pa-event__quick d-md-none">
        {item.date_description && (
          <div
            className="pa-small mb-2"
            dangerouslySetInnerHTML={{ __html: dateDescriptionHTML }}
          />
        )}
        {priceTopLine && (
          <p className="pa-small fw-bold mb-2">
            <i className="bi bi-currency-euro me-1" aria-hidden="true"></i>
            {priceTopLine}
          </p>
        )}
        <div className="d-flex gap-2">
          <div className="flex-grow-1">
            <SaveButton id={id} title={item.title} block />
          </div>
          <ShareButton title={item.title} text={item.lead_text} />
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-md-8 col-lg-9">
          {item.cover_url && (
            <figure className="pa-event__figure">
              <img
                src={item.cover_url}
                className="pa-event__img"
                alt={item.cover_alt || ""}
                loading="lazy"
              />
              {item.cover_credit && (
                <figcaption className="pa-event__credit">
                  Crédit : {item.cover_credit}
                </figcaption>
              )}
            </figure>
          )}

          <div
            className="pa-prose"
            dangerouslySetInnerHTML={{ __html: descriptionHTML }}
          />

          {item.updated_at && (
            <p className="pa-small pa-muted mt-3">
              Mis à jour le{" "}
              {new Date(item.updated_at).toLocaleDateString("fr-FR")}
            </p>
          )}

          <div className="pa-panel pa-panel--soft pa-event__share">
            <div className="flex-grow-1">
              <p className="pa-event__share-title">
                Cet événement vous intéresse ?
              </p>
              <p className="pa-small pa-muted mb-0">
                Partagez-le avec vos proches pour qu'ils en profitent aussi.
              </p>
            </div>
            <ShareButton title={item.title} text={item.lead_text} />
          </div>
        </div>

        <aside className="col-12 col-md-4 col-lg-3">
          <div className="pa-panel pa-event__summary pa-event__summary--sticky">
            <div className="d-grid gap-2 pa-event__actions">
              <SaveButton id={id} title={item.title} block />
              <ShareButton title={item.title} text={item.lead_text} />
            </div>

            {item.date_description && (
              <section>
                <h3>
                  <i className="bi bi-calendar me-1" aria-hidden="true"></i>
                  Quand
                </h3>
                <div
                  className="pa-small"
                  dangerouslySetInnerHTML={{ __html: dateDescriptionHTML }}
                />
              </section>
            )}

            {item.audience && (
              <section>
                <h3>
                  <i className="bi bi-people me-1" aria-hidden="true"></i>
                  Audience
                </h3>
                <p className="pa-small mb-0">{item.audience}</p>
              </section>
            )}

            {(priceTopLine || hasPriceDetail) && (
              <section>
                <h3>
                  <i className="bi bi-currency-euro me-1" aria-hidden="true"></i>
                  Tarif
                </h3>
                {priceTopLine && (
                  <p className="pa-small fw-bold mb-1">{priceTopLine}</p>
                )}
                {hasPriceDetail && (
                  <div
                    className="pa-small"
                    dangerouslySetInnerHTML={{ __html: priceDetailHTML }}
                  />
                )}
              </section>
            )}

            {item.access_link && (
              <section>
                <h3>
                  <i className="bi bi-ticket me-1" aria-hidden="true"></i>
                  Réservation
                </h3>
                <a
                  href={item.access_link}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="pa-btn pa-btn--primary pa-btn--sm pa-btn--block text-truncate"
                >
                  {item.access_link_text || "Réserver"}
                  <i
                    className="ms-1 bi bi-box-arrow-up-right"
                    aria-hidden="true"
                  ></i>
                </a>
              </section>
            )}

            {(item.address_city ||
              item.address_name ||
              item.address_url ||
              item.address_zipcode ||
              item.address_street) && (
              <section>
                <h3>
                  <i className="bi bi-geo-alt me-1" aria-hidden="true"></i>
                  Adresse
                </h3>
                <address className="pa-small fst-normal mb-2">
                  {item.address_name && <div>{item.address_name}</div>}
                  {item.address_street && <div>{item.address_street}</div>}
                  {(item.address_zipcode || item.address_city) && (
                    <div>
                      {item.address_zipcode} {item.address_city}
                    </div>
                  )}
                </address>
                {item.lat_lon && (
                  <MapContainer
                    className="rounded-2"
                    style={{ height: "180px", maxWidth: "100%" }}
                    center={[item.lat_lon.lat, item.lat_lon.lon]}
                    zoom={13}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[item.lat_lon.lat, item.lat_lon.lon]}
                      icon={
                        new Icon({
                          iconUrl: markerIconPng,
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                        })
                      }
                    />
                  </MapContainer>
                )}
              </section>
            )}

            {item.transport && (
              <section>
                <h3>
                  <i className="bi bi-bus-front me-1" aria-hidden="true"></i>
                  Transports
                </h3>
                <div
                  className="pa-small"
                  dangerouslySetInnerHTML={{ __html: transportHTML }}
                />
              </section>
            )}

            <section>
              <h3>
                <i
                  className="bi bi-universal-access-circle me-1"
                  aria-hidden="true"
                ></i>
                Accessibilité
              </h3>
              {accessibilityLabels.length > 0 ? (
                <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0">
                  {accessibilityLabels.map((p) => (
                    <li key={p.alt}>
                      <img
                        src={p.src}
                        alt={p.alt}
                        title={p.alt}
                        className="picto-access rounded-2 border"
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pa-small pa-muted fst-italic mb-0">
                  Non renseigné. Contactez l'organisateur pour plus d'informations.
                </p>
              )}
            </section>

            {(item.contact_facebook ||
              item.contact_mail ||
              item.contact_phone ||
              item.contact_twitter ||
              item.contact_url) && (
              <section>
                <h3>
                  <i className="bi bi-info-circle me-1" aria-hidden="true"></i>
                  Plus d'infos
                </h3>
                <ul className="list-unstyled small mb-0">
                  {item.contact_url && (
                    <li className="mb-1">
                      <a
                        href={item.contact_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-globe me-1" aria-hidden="true"></i>
                        Site web
                      </a>
                    </li>
                  )}
                  {item.contact_facebook && (
                    <li className="mb-1">
                      <a
                        href={item.contact_facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-facebook me-1" aria-hidden="true"></i>
                        Facebook
                      </a>
                    </li>
                  )}
                  {item.contact_twitter && (
                    <li className="mb-1">
                      <a
                        href={item.contact_twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-twitter me-1" aria-hidden="true"></i>
                        Twitter
                      </a>
                    </li>
                  )}
                  {item.contact_mail && (
                    <li className="mb-1 text-break">
                      <a href={`mailto:${item.contact_mail}`}>
                        <i
                          className="bi bi-envelope-at me-1"
                          aria-hidden="true"
                        ></i>
                        {item.contact_mail}
                      </a>
                    </li>
                  )}
                  {item.contact_phone && (
                    <li className="mb-1">
                      <a href={`tel:${item.contact_phone}`}>
                        <i
                          className="bi bi-telephone me-1"
                          aria-hidden="true"
                        ></i>
                        {item.contact_phone}
                      </a>
                    </li>
                  )}
                </ul>
              </section>
            )}
          </div>
        </aside>
      </div>
    </article>
  );
}

export default Event;
